<template>
    <div class="project-stages-container">
        <Dialog modal
                v-model:visible="isClientConfirmedOpen"
                :header="__('Potwierdzenie klienta')"
                :unstyled="false"
                :style="{ width: '400px' }"
                @hide="onClientConfirmedCancel(confirmedItem)"
                :pt="dialogPt">
            <v-form name="client-confirmed">
                <div class="grid"
                     style="margin-top:5px;margin-bottom:0">
                    <FormCalendar fieldName="end_date"
                                  label="Data zakończenia"
                                  :change="() => onToggleChange(true, confirmedItem)"
                                  v-model:field="confirmedItem.end_date" />
                </div>
                <span v-if="isError"
                      class="error-message-field">{{ errorMessage }}</span>

                <div class="grid"
                     style="margin-top:20px">
                    <Button @click.stop="onClientConfirmed(confirmedItem)"
                            :class="buttonClass('green')">
                        <template class="flex items-center gap-1"><i class="fa-regular fa-floppy-disk"></i><span>{{ __('Save') }}</span></template>
                    </Button>

                    <Button @click="onClientConfirmedCancel(confirmedItem)"
                            :class="buttonClass('gray')">
                        <template class="flex items-center gap-1"><i class="fa-regular fa-trash-can"></i><span>{{ __('Cancel') }}</span></template>
                    </Button>
                </div>
            </v-form>
        </Dialog>

        <div class="stages-header">
            <Button severity="secondary"
                    size="large"
                    class="m-3"
                    style="font-size:24px;"
                    @click="backToProject">
                <i class="fa-solid fa-diagram-project"></i><span v-html="backLabel"></span>
            </Button>
        </div>

        <Timeline v-if="stages.length > 0"
                  :value="stages"
                  class="project-timeline">
            <template #marker="{ item }">
                <span class="stage-marker"
                      :class="stateClass(item.state)">
                    <i :class="stateIcon(item.state)"
                       :style="stateStyle(item.state)"></i>
                </span>
            </template>

            <template #content="{ item }">
                <div class="stage-card"
                     :class="[stageBgClass(item), { 'stage-locked': item.state === 'L' }]">

                    <!-- Nagłówek etapu -->
                    <div class="stage-card-header">
                        <span class="stage-name">{{ item.stage_name }}</span>
                        <Tag :value="stateLabel(item.state)"
                             :style="stateLabelStyle(item.state)"
                             :severity="stateSeverity(item.state)" />
                    </div>

                    <!-- Formularz etapu (tylko gdy nie jest locked) -->
                    <template v-if="item.state !== 'L'">
                        <div class="stage-form">
                            <div class="grid stage-form-row">
                                <div class="stage-form-field field-responsible">
                                    <FormSelect class="w-full"
                                                label="Responsible person"
                                                fieldName="responsible_id"
                                                :isFormDisabled="item.state === 'C'"
                                                :options="users"
                                                v-model:field="item.responsible_id" />

                                </div>
                                <div class="stage-form-field field-date">
                                    <FormCalendar fieldName="start_date"
                                                  label="Begin date"
                                                  :isFormDisabled="item.state === 'C'"
                                                  v-model:field="item.start_date" />
                                </div>
                                <div class="stage-form-field field-date"
                                     v-if="item.is_client_confirmed">
                                    <FormCalendar fieldName="end_date"
                                                  label="End date"
                                                  :isFormDisabled="item.state === 'C'"
                                                  v-model:field="item.end_date" />

                                </div>
                            </div>

                            <div class="stage-form-row">
                                <div class="stage-checkbox">
                                    <FormInputToggleButton fieldName="is_client_confirmed"
                                                           label="Customer acceptance"
                                                           labelStyle="color:brown !important;"
                                                           :isFormDisabled="item.state === 'C'"
                                                           :change="(value) => onToggleChange(value, item)"
                                                           v-model:field="item.is_client_confirmed" />
                                </div>
                            </div>

                            <div class="stage-form-actions">
                                <Button v-if="item.state !== 'C'"
                                        :label="__('Save')"
                                        icon="fa-solid fa-save"
                                        style="font-size:20px"
                                        @click="saveStage(item)" />

                                <!-- Link do zewnętrznego formularza -->
                                <Button v-if="item.has_external_link && item.external_route_name"
                                        :label="'Otwórz ' + item.stage_name"
                                        icon="fa-solid fa-arrow-up-right-from-square"
                                        style="font-size:20px;color:olive;"
                                        severity="info"
                                        @click="openExternalLink(item)" />
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <p class="locked-info">
                            <i class="fa-solid fa-lock"
                               style="color:red"></i>
                            <i v-html="__('Stage is locked. Waiting on finish previous stage')" />
                        </p>
                    </template>

                </div>
            </template>
        </Timeline>

        <Message v-else-if="stages.length === 0"
                 severity="warn">
            Brak etapów dla tego projektu. Sprawdź czy umowa ma przypisaną usługę z etapami.
        </Message>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import Timeline from 'primevue/timeline'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import Checkbox from 'primevue/checkbox'
import Select from 'primevue/select'
import FormSelect from '@crud/FormSelect.vue'
import FormCalendar from '@crud/FormCalendar.vue'
import DatePicker from 'primevue/datepicker'
import FormCheckbox from '@crud/FormCheckbox.vue'
import FormInputToggleButton from '@crud/FormInputToggleButton.vue'
import ConfirmDialog from '@crud/ConfirmDialog.vue'
import axios from 'axios'
import { __, screenMessage, buttonClass } from '@helper/utils.js'
import { prepareSelect, onErrorService } from '@helper/crud_utils.js'

// -----------------------------------------------------

const props = defineProps({
    projectInfo: {
        type: Object,
        default: () => ({})
    },
    stages: {
        type: Array,
        default: () => []
    },
    projectId: {
        type: [Number, String],
        required: true
    },
    backToView: {
        type: Boolean,
        default: false
    },
    isFilteredAgreements: {
        type: Boolean,
        default: false
    },
})

// -----------------------------------------------------

// Lokalny stan
const stages = ref(props.stages.map(s => ({ ...s })))
const projectInfo = ref({ ...props.projectInfo })
const users = ref([])
const isClientConfirmedOpen = ref(false)
const confirmedItem = ref(null)
const isError = ref(false)
const errorMessage = ref('')

const backLabel = computed(() => {
    let label = __('Back to project')
    if (props.projectInfo.project_name) {
        label += ` <span style="font-weight: bold;color:green;">${props.projectInfo.project_name}</span>`
        if (props.projectInfo.service_name) {
            label += ` <span style="font-weight: bold;color:#ccc;font-size:14px">(${props.projectInfo.service_name})</span>`
        }
    }
    return label
})

const dialogPt = {
    header: { class: 'form-header' },
    content: { class: 'form-body' },
};

// -----------------------------------------------------

watch(() => props.stages, (val) => {
    if (val) stages.value = val.map(s => ({ ...s }))
})

watch(() => props.projectInfo, (val) => {
    if (val) projectInfo.value = { ...val }
})

// -----------------------------------------------------

onMounted(async () => {
    try {
        const options = await prepareSelect(['users'])
        users.value = options.users
    } catch (e) {
        users.value = []
    }
})

// -----------------------------------------------------

const saveStage = async (item) => {
    if (!item.project_stage_id) return

    try {
        const response = await axios.put(`/projects/project-stages/${item.project_stage_id}`, {
            responsible_id: item.responsible_id,
            start_date: item.start_date,
            end_date: item.end_date,
            is_client_confirmed: item.is_client_confirmed,
            state: item.state === 'P' ? 'I' : item.state,
        })

        if (response.data.success) {
            screenMessage({ type: 'success', title: __('Success'), message: __('Stage was updated successfully') });
        }
        router.reload({ only: ['stages'] })
    } catch (error) {
        onErrorService(error.response.data.errors, 'client-confirmed')
    }
}

// -----------------------------------------------------

const onToggleChange = (value, item) => {
    // Wywołaj dialog tylko gdy zmiana z Nie na Tak
    if (value === true) {
        confirmedItem.value = item
        isClientConfirmedOpen.value = true
    }
}

// -----------------------------------------------------

const onClientConfirmed = async (item) => {
    errorMessage.value = null
    if (!item.responsible_id) {
        errorMessage.value = __('Please select a responsible person')
    } else if (!item.start_date) {
        errorMessage.value = __('Please fill in the start date')
    } else if (!item.end_date) {
        errorMessage.value = __('Please fill in the end date')
    } else if (item.start_date > item.end_date) {
        errorMessage.value = __('End date must be after start date')
    }

    if (errorMessage.value) {
        isError.value = true
        return false
    }

    await saveStage(item)
    isClientConfirmedOpen.value = false
}

// -----------------------------------------------------

// Wywołane gdy użytkownik kliknie "Nie" w dialogu
const onClientConfirmedCancel = (item) => {
    if (item) {
        item.is_client_confirmed = false
    }
    isClientConfirmedOpen.value = false
    isError.value = false
    errorMessage.value = ''
}
// -----------------------------------------------------

const backToProject = () => {
    let data = {}
    if (props.isFilteredAgreements) {
        data = props.backToView ? { agreement_id: props.projectInfo.agreement_id } : { id: props.projectId, agreement_id: props.projectInfo.agreement_id }
    } else {
        data = props.backToView ? {} : { id: props.projectId }
    }

    router.visit(route('project-index', data), {
        onError: (errors) => {
            const message = errors?.error ?? 'Wystąpił nieznany błąd'
            screenMessage({ type: 'error', title: __('Error'), message: message });
        }
    })
}

// -----------------------------------------------------

const openExternalLink = (item) => {
    if (item.external_route_name) {
        router.visit(route(item.external_route_name, {
            project_id: props.projectId,
            backToView: props.backToView ? 1 : 0  // bool -> int, bo URL nie obsługuje bool
        }))
    }
}

// -----------------------------------------------------

const stateClass = (state) => ({
    'marker-locked': state === 'L',
    'marker-pending': state === 'P',
    'marker-in-progress': state === 'I',
    'marker-completed': state === 'C',
})
const stateStyle = (state) => {
    return state === 'L' ? 'color:red' : state === 'P' ? 'color:yellow' : state === 'I' ? 'color:blue' : state === 'C' ? 'color:green' : '';
}

const stateIcon = (state) => ({
    'L': 'fa-solid fa-lock',
    'P': 'fa-solid fa-clock',
    'I': 'fa-solid fa-spinner fa-spin',
    'C': 'fa-solid fa-check',
}[state] ?? 'fa-solid fa-circle')

const stateLabel = (state) => ({
    'L': 'Zablokowany',
    'P': 'Oczekujący',
    'I': 'W realizacji',
    'C': 'Zakończony',
}[state] ?? '')

const stateLabelStyle = (state) => ({
    'L': 'color:red;font-weight:600;',
    'P': 'color:orange;font-weight:600;',
    'I': 'color:blue;font-weight:600;',
    'C': 'color:green;font-weight:600;',
}[state] ?? '')

const stateSeverity = (state) => ({
    'L': 'secondary',
    'P': 'warn',
    'I': 'info',
    'C': 'success',
}[state] ?? 'secondary')

const stageBgClass = (item) => {
    // Przykład: wg statusu
    return {
        'bg-stage-locked': item.state === 'L',
        'bg-stage-pending': item.state === 'P',
        'bg-stage-inprogress': item.state === 'I',
        'bg-stage-completed': item.state === 'C',
        // Możesz dodać inne warunki, np. wg order:
        [`bg-stage-order-${item.order}`]: !!item.order,
    }
}

</script>

<style lang="scss" scoped>
.project-timeline {
    width: 50%;
    background-color: #f1f1f1;
    border-radius: 8px;
}

.bg-stage-locked {
    background: #eee !important;
}

.bg-stage-pending {
    background: #fffbe6 !important;
}

.bg-stage-inprogress {
    background: #e6f7ff !important;
}

.bg-stage-completed {
    background: #e6ffe6 !important;
}

// Przykład dla order
//.bg-stage-order-1 {
//  background: #f0f !important;
//}

//.bg-stage-order-2 {
//  background: #ff0 !important;
//}

.project-stages-container {
    padding: 20px;
}

.stages-header {
    margin-bottom: 30px;

    h2 {
        display: flex;
        align-items: center;
        gap: 12px;
    }
}

.service-badge {
    font-size: 0.75rem;
    background: var(--p-primary-color);
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
}

.stage-card {
    background: var(--p-surface-card);
    border: 1px solid var(--p-surface-border);
    border-radius: 8px;
    padding: 16px;
    margin: 10px 20px 10px 20px;

    min-width: 500px;

    /* Dodaj efekt wypukłości */
    box-shadow: 0 8px 24px 0 rgba(60, 60, 60, 0.18), 0 2px 8px 0 rgba(60, 60, 60, 0.12);

    &.stage-locked {
        opacity: 0.6;
        /* Możesz zmniejszyć cień dla zablokowanych, jeśli chcesz */
        box-shadow: 0 2px 4px 0 rgba(60, 60, 60, 0.10);
    }
}

.stage-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.stage-name {
    font-weight: 600;
    font-size: 1rem;
}

.stage-form-row {
    display: flex;
    gap: 24px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.stage-form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 180px;

    label {
        font-size: 0.85rem;
        color: var(--p-text-muted-color);
    }
}

.field-responsible {
    flex: 3 1 0;
}

.field-date {
    flex: 1 1 0;
}

.stage-form-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    justify-content: space-between;
}

.stage-checkbox {
    display: flex;
    align-items: center;
}

.locked-info {
    color: var(--p-text-muted-color);
    font-size: 16px;
    width: 100% !important;

    i {
        margin-right: 6px;
    }
}

.stage-marker {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    &.marker-locked {
        background: var(--p-surface-400);
    }

    &.marker-pending {
        background: var(--p-yellow-500);
    }

    &.marker-in-progress {
        background: var(--p-blue-500);
    }

    &.marker-completed {
        background: var(--p-green-500);
    }
}
</style>
